<script lang="ts" setup>
import {useCartStore} from "@/stores/cart";
import {price} from "@/filters/price";
import CartItemRow from "@/components/cart/CartItemRow.vue";
import {computed, onMounted, ref, watch} from "vue";
import type {TProduct} from "@/components/cart/CartPage.vue";
import type {TRequisite} from "@/components/profile/RequisiteItem.vue";
import {Select} from "primevue";
// @ts-ignore
import {Formie} from '@/../../vendor/verbb/formie/src/web/assets/frontend/src/js/formie-lib';
import ky from "ky";

import {useToast} from "primevue/usetoast";

const toast = useToast();

type TShippingMethod = {
  value: string,
  label: string,
  default: string
}

const props = defineProps<{
  products: TProduct[],
  shippingMethods: TShippingMethod[],
  requisites: TRequisite[]
}>()
const cartStore = useCartStore()

const isLoading = ref(false)
const product = (productId: string) => props.products.find((product: TProduct) => +product.id === +productId)
const isPriceColumnShown = computed(() => props.products.some((product: TProduct) => product.price))
const shippingMethod = ref(props.shippingMethods.find((shippingMethod => +shippingMethod.default > 0))?.value)
const selectedRequisites = ref(null)
const form = ref<HTMLDivElement | null>(null)
const requisites = ref<TRequisite[]>(props.requisites)
const md = ref<HTMLDivElement | null>(null)

watch(() => selectedRequisites.value, () => {
  form.value?.querySelectorAll('input').forEach((inputElement: HTMLInputElement) => {
    if (!inputElement.getAttribute('name')) {
      return
    }

    let name: keyof TRequisite | null = inputElement.getAttribute('name') as keyof TRequisite | null

    if (!name) {
      return;
    }

    if (name === 'entryTitle') {
      name = 'title'
    }

    if (name === 'phone[number]') {
      name = 'phone'
    }

    const _selectedRequisites = props.requisites.find((requisites) => requisites.id === selectedRequisites.value)

    if (typeof _selectedRequisites === 'undefined') {
      return
    }

    inputElement.value = _selectedRequisites[name].toString() ?? null
  })
})

const makeOrder = async () => {
  const body = new FormData()
  isLoading.value = true

  body.set('shippingMethod', shippingMethod.value as string)

  const _form = form.value?.querySelector('form')

  if (!_form) {
    return
  }

  const requisities = new FormData(_form)

  requisities.forEach((value, key) => {
    const data = {
      key,
      value
    }

    body.append('requisites[]', JSON.stringify(data))
  })

  cartStore.items.forEach((value, key) => {
    let comment = cartStore.getComment(+key)

    if (cartStore.getAnalog(+key)) {
      if (comment) {
        comment += ` (${cartStore.getAnalog(+key)})`
      } else {
        comment = cartStore.getAnalog(+key)
      }
    }

    const data = {
      id: key,
      comment,
      count: value.count,
      price: value.price ?? null
    }

    body.append('items[]', JSON.stringify(data))
  })

  try {
    await ky(`/orders`, {
      method: 'post',
      body
    })

    cartStore.clearCart()

    toast.add({
      severity: 'success',
      summary: 'Заказ успешно создан!',
      detail: 'Наши менеджеры свяжутся с вами в ближайшее время',
      life: 5000
    });
  } catch (_) {
    // empty
  } finally {
    isLoading.value = false
    _form.querySelector('[data-submit-action="submit"]')?.classList.remove('fui-loading')
    _form.querySelector('[data-submit-action="submit"]')?.removeAttribute('disabled')
  }
}

onMounted(() => {
  setTimeout(() => {
    const formElement = form.value?.querySelector('form')
    const formie = new Formie()

    formie.initForm(formElement)

    const _form = form.value?.querySelector('form')

    if (!_form) {
      return
    }

    form.value?.querySelector('form')?.addEventListener('onAfterFormieValidate', async (e) => {
      e.preventDefault()

      const body = new FormData(_form)

      try {
        const response = await ky(`/requisites${selectedRequisites.value ? '/' + selectedRequisites.value : ''}`, {
          method: 'post',
          body
        })

        const detail = await response.json()

        window.dispatchEvent(new CustomEvent('requisitesUpdate', {
          detail
        }))

        requisites.value = detail as TRequisite[]

        toast.add({
          severity: 'success',
          summary: selectedRequisites.value ? 'Реквизиты обновлены' : 'Новые реквизиты созданы',
          detail: 'Они будут использованы для этого заказа',
          life: 5000
        });
      } catch (_) {
        // empty
      } finally {
        _form.querySelector('[data-submit-action="submit"]')?.classList.remove('fui-loading')
        _form.querySelector('[data-submit-action="submit"]')?.removeAttribute('disabled')
      }
    })
  }, 0)
})
</script>

<template>
  <form v-if="!cartStore.isEmpty" ref="md" class="p-5 grid grid-cols-1 order">
    <h1 class="text-[6.563rem] leading-none font-centuryGothic font-bold uppercase">Оформление заказа</h1>
    <div class="grid grid-cols-2 gap-[1.25rem] mt-[3.75rem]">
      <div class="flex flex-col items-start">
        <div class="block">
          <h4 class="font-myriadPro leading-[90%]">Доставка</h4>
          <div class="flex gap-[0.625rem] mt-[1.25rem]">
            <button v-for="_shippingMethod in props.shippingMethods" :key="_shippingMethod.value"
                    :class="{'bg-[#3064C7] text-white': shippingMethod === _shippingMethod.value}"
                    class="inline-flex items-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-myriadPro justify-center text-xl min-h-[2.5rem] px-5 border rounded-none w-[14.5rem]"
                    type="button" @click="shippingMethod = _shippingMethod.value">{{ _shippingMethod.label }}
            </button>
          </div>
        </div>
        <div class="w-full  flex justify-between mt-[3.75rem]">
          <h4 class="font-myriadPro leading-[90%]">Оплата</h4>
          <Select v-model="selectedRequisites" :options="requisites" append-to="self" optionLabel="title"
                  optionValue="id" placeholder="Выберите реквизиты"/>
        </div>
        <div ref="form" class="mt-[1.25rem] pb-[3.75rem] font-myriadPro text-2xl w-full">
          <slot/>
        </div>
      </div>
      <div class="flex flex-col items-start">
        <div class="grid grid-cols-1 gap-2.5 mt-[1.875rem] font-myriadPro w-full ">
          <div class="flex flex-col border p-5 ">
            <div v-if="cartStore.totalSum" class="mb-5">
              <div class="flex justify-between font-semibold text-[2.375rem]">
                <h4>Итого</h4><h4>{{ price(cartStore.totalSum) }}</h4></div>
              <div class="flex justify-between text-2xl">
                <span>В том числе НДС (20%)</span><span>{{ price(cartStore.tax) }}</span>
              </div>
            </div>
            <button :disabled="isLoading"
                    class="inline-flex items-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center text-xl font-semibold leading-[90%] uppercase min-h-[3.438rem]"
                    type="button" @click="makeOrder">Отправить заявку
            </button>
            <p class="mt-[1.25rem]">Согласен с условиями
              <a class="text-[hsl(var(--roke-text-blue))]" href="/polzovatelskoe-soglashenie" target="_blank">Пользовательского
                соглашения</a>и
              <a class="text-[hsl(var(--roke-text-blue))]" href="/confidentiality" target="_blank">Политики
                конфиденциальности</a>
            </p></div>
        </div>
        <h4>У вас в заказе </h4>
        <table
          class="table-auto border-collapse desktop:border text-xl col-span-3 mt-2.5 desktop:mt-[30px] font-myriadPro h-fit">
          <thead class="max-desktop:hidden">
          <tr class="bg-[hsl(var(--roke-fill-grey))] leading-[90%]">
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]" colspan="2">Товар</th>
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Описание</th>
            <th class="border font-normal h-[53px] text-[20px] leading-[18px]">Комментарий</th>
            <th v-if="isPriceColumnShown" class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Цена</th>
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Количество</th>
            <th v-if="isPriceColumnShown" class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Итого</th>
          </tr>
          </thead>
          <tbody class="text-center text-2xl max-desktop:flex flex-col gap-2.5">
          <CartItemRow v-for="(item, n) in cartStore.items" :key="n" :product="product(item[0]) as TProduct"/>
          </tbody>
        </table>
      </div>
    </div>
  </form>
  <div v-else class="p-5 pt-[30px]">
    <div class="grid place-items-center min-h-[49.938rem] font-myriadPro">
      <div class="flex flex-col items-center gap-2.5">
        <h3 class="text-[38px] desktop:text-[2.5rem] leading-[90%] text-center">Вы не добавили ни одного товара</h3>
        <p class="text-[18px] desktop:text-xl leading-none text-center">Для оформления заказа добавьте товар в
          корзину</p>
        <a
          class="max-desktop:uppercase max-desktop:w-full inline-flex desktop:mt-2.5 items-center rounded-[3px] transition-colors disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center text-lg font-semibold h-14 px-5 hover:bg-white hover:text-[hsl(var(--roke-text-blue))]"
          href="/catalog">
          <span class="mt-0.5">Перейти в каталог</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep([data-pc-name="select"]) {
  @apply border border-[#D9D9D9] flex flex-row items-center justify-between gap-2.5 p-2.5 cursor-pointer transition-colors hover:border-[hsl(var(--roke-text-blue))] w-[250px];
}

:deep([data-pc-section="label"]) {
  @apply outline-none -translate-y-0.5;
}

:deep([data-pc-section="overlay"]) {
  @apply border border-[#D9D9D9] w-[250px] -translate-x-[1px] -translate-y-0.5;
}

:deep([data-pc-section="root"]) {
  @apply relative;
}
</style>
